import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Общий на сервере список Google Fonts для формы создания новости (мобильная и десктопная).
 */
export function usePersistedNewsGoogleFonts() {
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(false);
  const familiesRef = useRef([]);

  useEffect(() => {
    familiesRef.current = families;
  }, [families]);

  const load = useCallback(async () => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch('/api/news/google-font-families', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        if (Array.isArray(data.families)) setFamilies(data.families);
      }
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(async (nextList) => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) return false;
    const unique = [...new Set(nextList.map((f) => String(f).trim()).filter(Boolean))].slice(0, 200);
    try {
      const r = await fetch('/api/news/google-font-families', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ families: unique }),
      });
      if (!r.ok) return false;
      const data = await r.json().catch(() => ({}));
      if (Array.isArray(data.families)) setFamilies(data.families);
      else setFamilies(unique);
      return true;
    } catch {
      return false;
    }
  }, []);

  const addFamily = useCallback(
    async (rawName) => {
      const t = String(rawName || '').trim();
      if (!t) return;
      const prev = familiesRef.current;
      const key = t.toLowerCase();
      if (prev.some((x) => String(x).trim().toLowerCase() === key)) return;
      const ok = await save([...prev, t]);
      if (!ok) load();
    },
    [save, load]
  );

  const removeFamily = useCallback(
    async (rawName) => {
      const t = String(rawName || '').trim();
      if (!t) return;
      const prev = familiesRef.current;
      const next = prev.filter((x) => x !== t);
      if (next.length === prev.length) return;
      const ok = await save(next);
      if (!ok) load();
    },
    [save, load]
  );

  return { families, loading, load, save, addFamily, removeFamily };
}
